import {
  Deposit,
  DepositPayable,
  DepositType,
  FormCondition,
  Condition,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxRow,
  AgxSegmentPicker,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import { FormConditionOnDate } from '../components/FormConditionOnDate'
import FormConditionNDays from '../components/FormConditionNDays'
import { Images } from '@urbanx/agx-ui-components'
import { useCallback, useEffect, useState } from 'react'
import useDefaultDepositPayableValue from '../components/useDefaultDepositPayableValue'

interface EditConditionModalProps {
  id: string
  modalTitle: string
  formCondition: FormCondition | Deposit
  availableConditions?: Condition[] // Deprecated - use onDateConditions and fromDateConditions instead
  onDateConditions?: Condition[]
  fromDateConditions?: Condition[]
  onChange: (condition: FormCondition | Deposit) => void
  openModal: (_isOpen: boolean) => void
  depositType?: DepositType
}

const SegmentOptions = [
  { title: 'on...', value: DepositPayable.On },
  { title: '# days from...', value: DepositPayable.NDaysFrom },
]

export function EditConditionModal({
  id,
  modalTitle,
  formCondition,
  availableConditions,
  onDateConditions,
  fromDateConditions,
  onChange,
  openModal,
  depositType,
}: EditConditionModalProps) {
  const [value, setValue] = useState<FormCondition | Deposit>(
    formCondition as FormCondition | Deposit
  )
  const [internalValidate, setInternalValidate] = useState<boolean>(false)
  const [internalErrorMessage, setInternalErrorMessage] = useState<
    string | undefined
  >(undefined)

  const defaultSegment = useDefaultDepositPayableValue(formCondition)

  const [selectedSegment, setSelectedSegment] = useState<string>(
    () => defaultSegment
  )

  // For backward compatibility, use availableConditions if new props not provided
  const onDateOptions = onDateConditions || availableConditions || []
  const fromDateOptions = fromDateConditions || availableConditions || []

  useEffect(() => {
    setInternalValidate(false)
  }, [value])

  const onSelectedSegmentChange = useCallback(
    (segment: string) => {
      if (segment === selectedSegment) return

      setSelectedSegment(segment)
      setValue(
        (prev) =>
          ({
            ...prev,
            depositPayable: segment as DepositPayable,
          }) as FormCondition | Deposit
      )
    },
    [setSelectedSegment, setValue, selectedSegment]
  )

  const onFormConditionChange = useCallback(
    (condition: FormCondition | Deposit) => {
      setValue(
        (prev) =>
          ({
            ...prev,
            ...(condition as FormCondition & Deposit),
          }) as FormCondition | Deposit
      )
    },
    [setValue]
  )

  const onConfirmClick = useCallback(() => {
    setInternalValidate(true)
    if (!value || !value.condition) {
      setInternalErrorMessage('Select a condition')
      return
    } else if (
      value.condition === Condition.OnSpecificDate &&
      !value.referenceDate
    ) {
      setInternalErrorMessage('Select a date')
      return
    } else if (
      value.condition === Condition.NdaysFromDate &&
      !value.daysFromDate
    ) {
      setInternalErrorMessage('Enter number of days')
      return
    } else if (
      value.condition === Condition.UponSatisfactionOfSpecialCondition &&
      !value.specialConditionId
    ) {
      setInternalErrorMessage('Enter special condition clause')
      return
    }

    onChange(value)
    openModal(false)
  }, [onChange, value, openModal])

  return (
    <AgxModal
      title={modalTitle}
      variant="withDivider"
      type="single"
      actionButtonContainerStyle="withDivider"
      setIsDialogOpen={openModal}
      content={
        <AgxColumn
          gap="spacing-lg"
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxColumn id={id} gap="spacing-md" fill>
            <AgxColumn gap="spacing-xl" fill>
              <AgxSegmentPicker
                id={`${id}-depositPayableSegment`}
                segments={SegmentOptions}
                onChange={onSelectedSegmentChange}
                defaultValue={defaultSegment}
              />
              {selectedSegment === DepositPayable.On && (
                <FormConditionOnDate
                  id={id}
                  onSpecificDateOptions={onDateOptions}
                  defaultValue={value}
                  onValueChange={onFormConditionChange}
                  depositType={depositType}
                />
              )}
              {selectedSegment === DepositPayable.NDaysFrom && (
                <FormConditionNDays
                  id={id}
                  fromDateOptions={fromDateOptions}
                  defaultValue={value}
                  hasPriceOrPercentage={false}
                  onValueChange={onFormConditionChange}
                  validate={internalValidate}
                  depositType={depositType}
                />
              )}
            </AgxColumn>
          </AgxColumn>
          {internalValidate && internalErrorMessage && (
            <div className={'errorMessage'}>
              <Images.AlertCircle type="Danger" />
              <AgxTypography id={`${id}-error`} type="form_error" size="small">
                {internalErrorMessage}
              </AgxTypography>
            </div>
          )}
        </AgxColumn>
      }
      actionButtons={
        <AgxRow gap="spacing-md" alignItems="flex-end" fill>
          <AgxButton
            id="cancel-add-finance"
            variant="hollow"
            text="Cancel"
            size="large"
            stretch
            onClick={() => openModal(false)}
          />
          <AgxButton
            id="save-add-finance"
            variant="primary"
            text="Save"
            size="large"
            stretch
            onClick={onConfirmClick}
            disabled={internalValidate && !!internalErrorMessage}
          />
        </AgxRow>
      }
    />
  )
}
